"use client"
import { Link, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import useSidebarToggle from "../../hooks/useSidebarToggle"
import { logout, getAdminProfile, updateAdminProfile, isAuthenticated, getToken } from "../../api/auth"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.voyagian.com"

export default function Header() {
  useSidebarToggle()
  const navigate = useNavigate()

  const [adminProfile, setAdminProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  const [showEditModal, setShowEditModal] = useState(false)
  const [editName, setEditName] = useState("")
  const [editImage, setEditImage] = useState(null)
  const [imagePreview, setImagePreview] = useState("")
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState("")
  const [updateSuccess, setUpdateSuccess] = useState("")

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Check if token exists before attempting to fetch profile
        if (!isAuthenticated() || !getToken()) {
          console.log("[v0] No token available, skipping profile fetch")
          setLoading(false)
          return
        }

        console.log("[v0] Fetching admin profile...")
        const response = await getAdminProfile()
        if (response.success) {
          console.log("[v0] Profile fetched successfully")
          setAdminProfile(response.data)
        }
      } catch (error) {
        console.error("[v0] Failed to fetch admin profile:", error)
        // Token issues are handled by the auth module's automatic redirect
        // Just log and gracefully handle here
      } finally {
        setLoading(false)
      }
    }

    // Only fetch if we're authenticated
    if (isAuthenticated()) {
      fetchProfile()
    } else {
      setLoading(false)
    }
  }, [])

  const handleLogout = (e) => {
    e.preventDefault()
    logout()
    navigate("/admin/login", { replace: true })
  }

  const getProfileImageUrl = () => {
    if (adminProfile?.profileImage) {
      return `${API_BASE_URL}${adminProfile.profileImage}`
    }
    return "/assets/img/profiles/avatar-02.jpg"
  }

  const formatRole = (role) => {
    if (!role) return "Admin"
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const handleOpenEditModal = (e) => {
    e.preventDefault()
    if (!adminProfile) {
      console.log("[v0] Profile not loaded yet")
      return
    }
    setEditName(adminProfile.name || "")
    setImagePreview(getProfileImageUrl())
    setEditImage(null)
    setUpdateError("")
    setUpdateSuccess("")
    setShowEditModal(true)
  }

  const handleCloseModal = () => {
    setShowEditModal(false)
    setEditImage(null)
    setImagePreview("")
    setUpdateError("")
    setUpdateSuccess("")
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setEditImage(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setUpdating(true)
    setUpdateError("")
    setUpdateSuccess("")

    try {
      const profileData = {
        name: editName,
      }

      if (editImage) {
        profileData.profileImage = editImage
      }

      const response = await updateAdminProfile(profileData)

      if (response.success) {
        setAdminProfile(response.data)
        setUpdateSuccess("Profile updated successfully!")
        setTimeout(() => {
          handleCloseModal()
        }, 1500)
      }
    } catch (error) {
      setUpdateError(error.message || "Failed to update profile")
    } finally {
      setUpdating(false)
    }
  }

  return (
    <>
      <div className="header header-one">
        <Link
          to="/admin"
          className="d-inline-flex d-sm-inline-flex align-items-center d-md-inline-flex d-lg-none align-items-center device-logo"
        >
          <img src="/assets/img/logo.png" className="img-fluid logo2" alt="Logo" style={{ width: 60 }} />
        </Link>

        <div className="main-logo d-inline float-start d-lg-flex align-items-center d-none d-sm-none d-md-none">
          <div className="logo-color">
            <Link to="/admin">
              <h4 className="img-fluid logo-blue text-white fw-bold">Super Admin</h4>
            </Link>
            <Link to="/admin">
              <h4 className="img-fluid logo-small"></h4>
            </Link>
          </div>
        </div>

        <Link to="#" id="toggle_btn">
          <span className="toggle-bars">
            <span className="bar-icons"></span>
            <span className="bar-icons"></span>
            <span className="bar-icons"></span>
            <span className="bar-icons"></span>
          </span>
        </Link>

        <div className="top-nav-search">
          <form onSubmit={(e) => e.preventDefault()}>
            <input type="text" className="form-control" placeholder="Search here" />
            <button className="btn" type="submit">
              <img src="/assets/img/icons/search.svg" alt="img" />
            </button>
          </form>
        </div>

        <Link className="mobile_btn" id="mobile_btn">
          <i className="fas fa-bars"></i>
        </Link>

        <ul className="nav nav-tabs user-menu">
          <li className="nav-item dropdown">
            <Link to="#" className="user-link nav-link" data-bs-toggle="dropdown">
              <span className="user-img">
                <img src={getProfileImageUrl() || "/placeholder.svg"} alt="img" className="profilesidebar" />
                <span className="animate-circle"></span>
              </span>
              <span className="user-content">
                <span className="user-details">{loading ? "Loading..." : formatRole(adminProfile?.role)}</span>
                <span className="user-name">{loading ? "..." : adminProfile?.name || "Admin"}</span>
              </span>
            </Link>

            <div className="dropdown-menu menu-drop-user">
              <div className="profilemenu">
                <div className="subscription-logout">
                  <ul>
                    <li>
                      <Link
                        className={`dropdown-item ${!adminProfile ? "disabled" : ""}`}
                        to="#"
                        onClick={handleOpenEditModal}
                        style={!adminProfile ? { opacity: 0.5, pointerEvents: "none" } : {}}
                      >
                        <i className="fas fa-user-edit me-2"></i>
                        {loading ? "Loading..." : "Edit Profile"}
                      </Link>
                    </li>
                    <li className="pb-0">
                      <Link className="dropdown-item" to="#" onClick={handleLogout}>
                        <i className="fas fa-sign-out-alt me-2"></i>
                        Log Out
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </li>

          <li className="nav-item">
            <button id="themeToggle" className="theme-toggle" title="Toggle Dark/Light" style={{ border: "none" }}>
              <i className="bi bi-moon-stars-fill"></i>
            </button>
          </li>
        </ul>
      </div>

      {showEditModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={handleCloseModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Profile</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal} aria-label="Close"></button>
              </div>
              <form onSubmit={handleUpdateProfile}>
                <div className="modal-body">
                  {updateError && (
                    <div className="alert alert-danger" role="alert">
                      {updateError}
                    </div>
                  )}
                  {updateSuccess && (
                    <div className="alert alert-success" role="alert">
                      {updateSuccess}
                    </div>
                  )}

                  <div className="text-center mb-4">
                    <div className="position-relative d-inline-block">
                      <img
                        src={imagePreview || "/placeholder.svg"}
                        alt="Profile"
                        className="rounded-circle"
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          border: "3px solid #e9ecef",
                        }}
                      />
                      <label
                        htmlFor="profileImageInput"
                        className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "36px",
                          height: "36px",
                          cursor: "pointer",
                          border: "2px solid white",
                        }}
                      >
                        <i className="fas fa-camera"></i>
                      </label>
                      <input
                        type="file"
                        id="profileImageInput"
                        className="d-none"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: "13px" }}>
                      Click camera icon to change photo
                    </p>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="editName" className="form-label">
                      Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editName"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Enter your name"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={adminProfile?.email || ""}
                      disabled
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                    <small className="text-muted">Email cannot be changed</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={updating}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
